import {
  Button,
  FormGroup,
  InputGroup,
  TextArea,
  Card,
  Elevation,
  Intent,
  Classes,
  HTMLTable,
} from '@blueprintjs/core';
import { DateInput3 } from '@blueprintjs/datetime2';
import * as React from 'react';
import { useStore } from 'react-redux';

import { handleInputChange } from '@kotify/rehelpers';
import { showAxiosError } from 'vms/app/AppToaster';
import { useAppSelector } from 'vms/app/hooks';
import type { RootState } from 'vms/app/rootReducer';
import FormHelper from 'vms/common/components/FormHelper';
import { formatDate, reformatDTZ, parseDateTime } from 'vms/common/utils';
import { UserName } from 'vms/features/accounts/queries';
import type { DialogProps } from 'vms/features/dialog/types';
import { selectLocations } from 'vms/features/locations/selectors';
import { PART_DELETE_SUCCESS } from 'vms/features/parts/actions';
import * as api from 'vms/features/parts/api';
import PartStatusSelect from 'vms/features/parts/PartStatusSelect';
import { PartVendorSelect } from 'vms/features/parts/queries';
import { selectPartDefinitions } from 'vms/features/parts/selectors';
import { formatPartFullName } from 'vms/features/parts/services';
import type {
  Part,
  PartEditDialogEditableData,
  PartEditDialogReadOnlyData,
} from 'vms/features/parts/types';
import { refreshVehicle } from 'vms/features/vehicles/actions';

type Props = DialogProps<
  PartEditDialogEditableData,
  PartEditDialogReadOnlyData,
  ValidationErrors<Part>
>;

const MAX_DATE = new Date(new Date().getFullYear() + 1, 12, 31);

export default function PartEditDialog(props: Props) {
  const { dispatch, getState } = useStore<RootState>();
  const locations = useAppSelector(selectLocations);
  const definitions = useAppSelector(selectPartDefinitions);
  const { isInProgress } = props;
  const { image } = props.readOnlyData;
  const data = props.editableData;
  const formHelper = new FormHelper<Part>(props.errors);

  const handleChange = (
    event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>
  ) => {
    props.handleChange(handleInputChange(event));
    props.setErrors({});
  };

  const handleChangeETA = (eta: string | null) => {
    props.handleChange({ eta });
    props.setErrors({});
  };

  const handleDelete = () => {
    api
      .deletePart(data.id)
      .then(() => {
        dispatch({ type: PART_DELETE_SUCCESS, payload: { part: data } });
        refreshVehicle(dispatch, getState, data.vehicle.id);
      })
      .then(props.onCancel, showAxiosError);
  };

  return (
    <>
      <div className={Classes.DIALOG_BODY}>
        {image && (
          <Card elevation={Elevation.TWO} style={{ width: '100%', marginBottom: '20px' }}>
            <img src={image} alt="GPS Image" width="100%" height="auto" />
          </Card>
        )}

        <HTMLTable className="disable-form-group-margin">
          <tbody>
            <tr>
              <td style={{ minWidth: '200px' }}>VIN</td>
              <td>{data.vehicle.vin}</td>
            </tr>

            <tr>
              <td>Make</td>
              <td>{data.vehicle.make}</td>
            </tr>

            <tr>
              <td>Model</td>
              <td>{data.vehicle.model}</td>
            </tr>

            <tr>
              <td>Technician</td>
              <td>
                <UserName id={data.created_by} />
              </td>
            </tr>

            <tr>
              <td>Location</td>
              <td>{data.location && locations[data.location].name}</td>
            </tr>

            <tr>
              <td>Part Name</td>
              <td>{formatPartFullName(data, definitions)}</td>
            </tr>
            {data.ordered_by && data.ordered_at && (
              <tr>
                <td>Ordered</td>
                <td>
                  By <UserName id={data.ordered_by} /> at {reformatDTZ(data.ordered_at)}
                </td>
              </tr>
            )}
            {data.received_by && data.received_at && (
              <tr>
                <td>Received</td>
                <td>
                  By <UserName id={data.received_by} /> at {reformatDTZ(data.received_at)}
                </td>
              </tr>
            )}

            <tr>
              <td>Part#</td>
              <td>
                <FormGroup {...formHelper.getErrors('number')} inline={true}>
                  <InputGroup
                    name="number"
                    value={data.number}
                    onChange={handleChange}
                    intent={formHelper.getIntent('number')}
                    maxLength={50}
                  />
                </FormGroup>
              </td>
            </tr>

            <tr>
              <td>Status</td>
              <td>
                <FormGroup {...formHelper.getErrors('status')} inline={true}>
                  <PartStatusSelect status={data.status} handleChange={handleChange} />
                </FormGroup>
              </td>
            </tr>

            <tr>
              <td>Vendor</td>
              <td>
                <FormGroup {...formHelper.getErrors('vendor')} inline={true}>
                  <PartVendorSelect
                    selectedId={data.vendor}
                    handleChange={handleChange}
                  />
                </FormGroup>
              </td>
            </tr>

            <tr>
              <td>ETA / Due</td>
              <td>
                <FormGroup {...formHelper.getErrors('eta')} inline={true}>
                  <DateInput3
                    maxDate={MAX_DATE}
                    formatDate={(date: Date) => formatDate(date, true)}
                    onChange={handleChangeETA}
                    parseDate={(str) => parseDateTime(str, false)}
                    placeholder={'MM/DD/YYYY'}
                    value={data.eta}
                  />
                </FormGroup>
              </td>
            </tr>

            <tr>
              <td>Price</td>
              <td>
                <FormGroup {...formHelper.getErrors('price')} inline={true}>
                  <InputGroup
                    name="price"
                    value={data.price || ''}
                    onChange={handleChange}
                    intent={formHelper.getIntent('price')}
                  />
                </FormGroup>
              </td>
            </tr>

            <tr>
              <td>Note</td>
              <td>
                <FormGroup {...formHelper.getErrors('notes')} inline={true}>
                  <TextArea
                    name="notes"
                    value={data.notes}
                    onChange={handleChange}
                    intent={formHelper.getIntent('notes')}
                    rows={4}
                  />
                </FormGroup>
              </td>
            </tr>
          </tbody>
        </HTMLTable>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div
          className={Classes.DIALOG_FOOTER_ACTIONS}
          style={{ justifyContent: 'space-between' }}
        >
          <Button
            text="Delete"
            loading={isInProgress}
            onClick={handleDelete}
            intent={Intent.DANGER}
          />
          <Button text="Save" loading={isInProgress} onClick={props.onOk} />
        </div>
      </div>
    </>
  );
}
